import type { GDTApiErrors } from '@/types';
import type {
  InviteUserByIdResponse,
  ResponseInvitarAmigo,
  ResponseSugerirUsuariosGCA,
  TournamentInviteFormData,
} from '@/types/invites';
import { axiosErrorHandler } from '@/utils';
import axiosGdt from '@/utils/axiosGdt';
import { URLs } from '@/utils/urls';

// Interfaz para los datos del formulario de invitación de Copa de Amigos
interface FriendsCupInviteFormData {
  idGrupo: number;
  names?: string[];
  emails?: string[];
}

// Servicio para invitar amigos por email
const inviteFriendsByEmail = axiosErrorHandler(
  async (data: FriendsCupInviteFormData) => {
    try {
      // Construimos los parámetros SIN incluir _action (ya está en la URL base)
      const params = new URLSearchParams({
        enviar: '1',
        idGrupo: data.idGrupo.toString(),
      });

      // Si tenemos nombres y emails, los agregamos a los parámetros
      if (data.names && data.emails) {
        params.append('nombre', data.names.join('|'));
        params.append('mail', data.emails.join('|'));
      }

      // Verificamos si la URL base ya contiene un signo de interrogación
      const baseUrl = URLs.COPA_AMIGOS.INVITAR_AMIGOS_MAIL;
      const separator = baseUrl.includes('?') ? '&' : '?';

      // Construimos la URL completa
      const url = `${baseUrl}${separator}${params.toString()}`;
      const response = await axiosGdt.get(url);

      return response.data;
    } catch (error: any) {
      // Log detallado del error

      // Si es un error de Axios, mostramos más detalles
      if (error.response) {
        return {
          status: error.response.status,
          statusText: error.response.statusText,
          data: error.response.data,
        };
      }

      // Si es un error de configuración
      if (error.config) {
        return {
          url: error.config.url,
          method: error.config.method,
          headers: error.config.headers,
          params: error.config.params,
        };
      }

      throw error; // Re-lanzamos el error para que lo maneje axiosErrorHandler
    }
  }
);

// Nuevo servicio para invitar a un DT por ID
const inviteDTById = axiosErrorHandler(
  async (idGrupo: number, idUsuarioDT: number) => {
    try {
      // Usar directamente la URL de COPA_AMIGOS.INVITAR_DT
      const url = URLs.COPA_AMIGOS.INVITAR_DT;

      // Parámetros requeridos según la documentación
      const params = new URLSearchParams({
        idGrupo: idGrupo.toString(),
        idUsuarioDT: idUsuarioDT.toString(),
      });

      // Realizamos la petición POST con los parámetros en la URL
      const response = await axiosGdt.post(`${url}?${params.toString()}`);

      return response.data;
    } catch (error: any) {
      // Si es un error de Axios, devolvemos detalles
      if (error.response) {
        return {
          estado: false,
          mensaje: error.response.data?.mensaje || 'Error al invitar al DT',
        };
      }

      return {
        estado: false,
        mensaje: 'Error al invitar al DT',
      };
    }
  }
);

// Nuevo servicio para sugerir usuarios
const suggestUsers = axiosErrorHandler(async (cantMaxResultados = 3) => {
  try {
    // Usar directamente la URL de COPA_AMIGOS.SUGERIR_USUARIOS
    const url = URLs.COPA_AMIGOS.SUGERIR_USUARIOS;

    // Parámetros opcionales según la documentación
    const params = new URLSearchParams({
      cantMaxResultados: cantMaxResultados.toString(),
    });

    // Realizamos la petición GET
    const response = await axiosGdt.get(`${url}?${params.toString()}`);

    return response.data;
  } catch (error: any) {
    // Si es un error de Axios, devolvemos detalles
    if (error.response) {
      return {
        estado: false,
        mensaje:
          error.response.data?.mensaje || 'Error al obtener usuarios sugeridos',
        usuariosSugeridos: [],
      };
    }

    return {
      estado: false,
      mensaje: 'Error al obtener usuarios sugeridos',
      usuariosSugeridos: [],
    };
  }
});

// Nuevo servicio para buscar DTs por nombre
const searchDtsByName = axiosErrorHandler(
  async (searchTerm: string, idGrupo: number) => {
    try {
      // Usar la URL de COPA_AMIGOS.BUSCAR_DTS
      const url = URLs.COPA_AMIGOS.BUSCAR_DTS;

      // Parámetros requeridos según la documentación
      const params = new URLSearchParams({
        search: searchTerm,
        idGrupo: idGrupo.toString(),
      });

      // Realizamos la petición GET
      const response = await axiosGdt.get(`${url}?${params.toString()}`);

      return response.data;
    } catch (error: any) {
      // Si es un error de Axios, devolvemos detalles
      if (error.response) {
        return {
          estado: false,
          mensaje: error.response.data?.mensaje || 'Error al buscar DTs',
          usuarios: [],
        };
      }

      return {
        estado: false,
        mensaje: 'Error al buscar DTs',
        usuarios: [],
      };
    }
  }
);

// Adaptador para el componente GenericInviteFriends
export const createFriendsCupInviteServices = () => {
  return {
    // Adaptador para invitar por email
    inviteByEmail: async (
      data: TournamentInviteFormData
    ): Promise<ResponseInvitarAmigo | GDTApiErrors> => {
      try {
        // Convertir TournamentInviteFormData a FriendsCupInviteFormData
        const friendsCupData: FriendsCupInviteFormData = {
          idGrupo: data.idGrupo || 0,
          names: data.names,
          emails: data.emails,
        };

        // Llamar al servicio
        const response = await inviteFriendsByEmail(friendsCupData);

        // Verificar si response.estado es true
        const isSuccess =
          response && typeof response === 'object' && response.estado === true;

        // Crear un nuevo objeto adaptado
        const adaptedResponse: ResponseInvitarAmigo = {
          estado: true,
          mensaje: isSuccess
            ? response.mensaje || 'Tu invitación por mail fue enviada.'
            : response.mensaje ||
              response.message ||
              'Tu invitación por mail fue enviada.',
        };

        return adaptedResponse;
      } catch (error) {
        return {
          estado: false,
          mensaje: 'Error al enviar la invitación',
        };
      }
    },

    // Implementación del servicio inviteById
    inviteById: async (
      idGrupo: number,
      idUsuarioDT: number
    ): Promise<InviteUserByIdResponse | GDTApiErrors> => {
      try {
        // Llamar al servicio
        const response = await inviteDTById(idGrupo, idUsuarioDT);
        return response;
      } catch (error) {
        return {
          estado: false,
          mensaje: 'Error al invitar al DT',
          // invPendiente: false,
        };
      }
    },

    // Implementación del servicio suggestUsers
    suggestUsers: async (
      cantMaxResultados = 10
    ): Promise<ResponseSugerirUsuariosGCA | GDTApiErrors> => {
      try {
        // Llamar al servicio
        const response = await suggestUsers(cantMaxResultados);

        // Verificar si response.estado es true
        const isSuccess =
          response && typeof response === 'object' && response.estado === true;

        if (isSuccess && Array.isArray(response.usuariosSugeridos)) {
          // Adaptar la respuesta
          return {
            estado: true,
            usuariosSugeridos: response.usuariosSugeridos.map((user: any) => ({
              id: user.idUsuario,
              nombre: `${user.nombres} ${user.apellido}`,
              ...user,
            })),
          };
        } else {
          // Si no hay usuarios o la respuesta no tiene el formato esperado
          return {
            estado: true,
            usuariosSugeridos: [],
          };
        }
      } catch (error) {
        return {
          estado: false,
          mensaje: 'Error al obtener usuarios sugeridos',
          usuariosSugeridos: [],
        };
      }
    },

    // Implementación del servicio searchByName
    searchByName: async (searchTerm: string, idGrupo: number) => {
      try {
        // Llamar al servicio
        const response = await searchDtsByName(searchTerm, idGrupo);

        // Verificar si response.estado es true
        const isSuccess =
          response && typeof response === 'object' && response.estado === true;

        if (isSuccess) {
          // La respuesta ya tiene el formato esperado
          return response;
        } else {
          // Si hay un error, devolver una respuesta con usuarios vacíos
          return {
            estado: false,
            mensaje: response.mensaje || 'No se encontraron resultados',
            usuarios: [],
          };
        }
      } catch (error) {
        return {
          estado: false,
          mensaje: 'Error al buscar DTs',
          usuarios: [],
        };
      }
    },
  };
};
